import React, { useState } from 'react';
import { Table, Button, Input, InputGroup, Panel, Form, Toggle, Message } from 'rsuite';
import { Icon } from '@rsuite/icons';
import { MdContentCopy } from 'react-icons/md';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel } from '@/models/core.model';
import { EnergyHub, EnergyHubCheckInTag } from '@/models/energy-hub.model';
import { createEnergyHubTag, setEnergyHubTagActive } from '@/services/energy-hub.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;

interface Props {
  hub: EnergyHub;
  onChanged: () => void;
}

const EnergyHubTagList: React.FC<Props> = ({ hub, onChanged }) => {
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);

  const handleCreate = async () => {
    setErrorModel(undefined);
    setSaving(true);
    try {
      const result = await createEnergyHubTag(hub.id, label.trim() || undefined);
      if (result.success) {
        toastSuccessMessage('Tag created');
        setLabel('');
        onChanged();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row: EnergyHubCheckInTag, isActive: boolean) => {
    if (!isActive && !window.confirm('Deactivate this tag? Scans of it will stop working.')) return;
    try {
      await setEnergyHubTagActive(hub.id, row.id, isActive);
      onChanged();
    } catch (err) {
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in updating tag: ${em?.errorMessage}`);
    }
  };

  const copy = (url: string) => {
    navigator.clipboard.writeText(url);
    toastSuccessMessage('Check-in URL copied');
  };

  return (
    <PageContent>
      <Message type="info" showIcon style={{ marginBottom: 16 }}>
        Print each URL as a QR code and/or write it to an NFC sticker at the counter. A runner scanning it checks in.
        If a tag leaks (e.g. a photo is posted online), deactivate it and create a new one.
      </Message>
      <Panel bordered header="New tag" style={{ marginBottom: 16 }}>
        <Form layout="inline">
          <Form.Group>
            <Input placeholder="Label (e.g. Counter QR, Table NFC)" value={label} maxLength={200}
              onChange={setLabel} style={{ width: 320 }} />
          </Form.Group>
          <Button appearance="primary" onClick={handleCreate} loading={saving}>Create tag</Button>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 12 }} />}
      </Panel>

      <Table autoHeight data={hub.checkInTags} rowHeight={56}>
        <Column flexGrow={0.4}>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={1.2} fullText>
          <HeaderCell>Label</HeaderCell>
          <Cell>{(row: EnergyHubCheckInTag) => row.label || '-'}</Cell>
        </Column>
        <Column flexGrow={3}>
          <HeaderCell>Check-in URL</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(row: EnergyHubCheckInTag) => (
              <InputGroup>
                <Input value={row.checkInUrl} readOnly />
                <InputGroup.Button onClick={() => copy(row.checkInUrl)}><Icon as={MdContentCopy} /></InputGroup.Button>
              </InputGroup>
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.7} align="center">
          <HeaderCell>Active</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(row: EnergyHubCheckInTag) => <Toggle checked={row.isActive} onChange={v => handleToggle(row, v)} />}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Created At</HeaderCell>
          <Cell fullText>{(row: EnergyHubCheckInTag) => jsDateToZone(row.createdAt)}</Cell>
        </Column>
      </Table>
    </PageContent>
  );
};

export default EnergyHubTagList;
